import { EventNames } from '../../../types/misc.types'
import { trackEvent } from '../../../utils/appBridge'

/** Distinct `screenType` / `eventId` roots per surface (landing vs dashboard vs family variants). */
export const NewEliteLandingAnalyticsScreen = {
  Landing: 'NewElite_Landing',
  Dashboard: 'NewElite_Dashboard',
  FamilyLanding: 'NewElite_Family_Landing',
  FamilyDashboard: 'NewElite_Family_Dashboard'
} as const

export type NewEliteLandingAnalyticsScreenType =
  (typeof NewEliteLandingAnalyticsScreen)[keyof typeof NewEliteLandingAnalyticsScreen]

export const NewEliteLandingBottomSheetName = {
  BenefitsInfo: 'benefits_info',
  EsignLearnMore: 'esign_learn_more',
  EliteYearlyUpgrade: 'elite_yearly_upgrade',
  RestrictedAccessInfo: 'restricted_access_info',
  LockDownTimerEndsInfo: 'lockdown_timer_ends_info'
} as const

export type NewEliteLandingBottomSheetNameType =
  (typeof NewEliteLandingBottomSheetName)[keyof typeof NewEliteLandingBottomSheetName]

export const NewEliteLandingEventId = {
  WebviewBack: 'webview_back',
  WebviewClose: 'webview_close',
  PlanToggle: 'plan_toggle',
  PrimaryCta: 'primary_cta',
  PrimaryCtaDisabled: 'primary_cta_disabled',
  KycConsentToggle: 'kyc_consent_toggle',
  EsignLearnMoreOpen: 'esign_learn_more_open',
  BenefitInfoOpen: 'benefit_info_open',
  BenefitInfoTab: 'benefit_info_tab',
  KeyFeatureLearnMore: 'key_feature_learn_more',
  InsightTile: 'insight_tile',
  RenewalCta: 'renewal_cta',
  UpgradeOpenSheet: 'upgrade_open_sheet',
  UpgradeContinuePayment: 'upgrade_continue_payment',
  UpgradeAdjustToggle: 'upgrade_adjust_toggle',
  RebalancingTimerCta: 'rebalancing_timer_cta',
  RebalancingPlanCta: 'rebalancing_plan_cta',
  GoToPortfolioLocked: 'go_to_portfolio_locked',
  GoToPortfolioGracePeriod: 'go_to_portfolio_grace_period',
  RestrictedAccessInfoOpen: 'restricted_access_info_open',
  LockDownTimerEndsInfoOpen: 'lockdown_timer_ends_info_open',
  AboutVideoTrustMarker: 'elite_about_video_trust_marker',
  AboutVideoFab: 'elite_about_video_fab'
} as const

type AnalyticsPayload = Record<string, string | number | boolean | undefined>

function buildPayload(
  screen: NewEliteLandingAnalyticsScreenType,
  eventId: string,
  extra?: AnalyticsPayload
): AnalyticsPayload {
  return {
    screenType: screen,
    eventId,
    ...extra
  }
}

export function trackNewEliteScreenLoaded(
  screen: NewEliteLandingAnalyticsScreenType,
  extra?: AnalyticsPayload
): void {
  trackEvent({
    eventName: EventNames.ScreenLoaded,
    payload: buildPayload(screen, screen, extra)
  })
}

export function trackNewEliteBottomSheetLoaded(
  screen: NewEliteLandingAnalyticsScreenType,
  bottomSheetName: NewEliteLandingBottomSheetNameType,
  extra?: AnalyticsPayload
): void {
  trackEvent({
    eventName: EventNames.BottomSheetLoad,
    payload: buildPayload(screen, bottomSheetName, {
      bottomSheetName,
      ...extra
    })
  })
}

export function trackNewEliteUserClicked(
  screen: NewEliteLandingAnalyticsScreenType,
  eventId: string,
  extra?: AnalyticsPayload
): void {
  trackEvent({
    eventName: EventNames.UserClicked,
    payload: buildPayload(screen, eventId, extra)
  })
}

export function trackNewEliteButtonTap(
  screen: NewEliteLandingAnalyticsScreenType,
  eventId: string,
  extra?: AnalyticsPayload
): void {
  trackEvent({
    eventName: EventNames.ButtonTap,
    payload: buildPayload(screen, eventId, extra)
  })
}
